import {
    Activity,
    Bell,
    CalendarClock,
    CalendarX,
    CircleHelp,
    Cpu,
    Database,
    FileText,
    Hash,
    HeartHandshake,
    History,
    Languages,
    MessageSquare,
    Moon,
    PhoneCall,
    Puzzle,
    SkipForward,
    Sparkles,
    SquarePen,
    Tag,
    Users,
    Zap,
} from 'lucide-react';
import { type ComponentType } from 'react';

import { PhoebeFlowerIcon } from '@apps/web/components/settings/phoebe_flower_icon';

type SettingsNavGate =
    | 'agentV3'
    | 'coordinatorAutomations'
    | 'canAccessDataSync'
    | 'internalAdmin'
    | 'caregiverEngagement'
    | 'careCoordinator'
    | 'clockInOut'
    | 'dailyDigest'
    | 'phoneSetup'
    | 'slack'
    | 'multiOffice';

export type SettingsKeywordSetting = {
    readonly section: string;
    readonly keywords?: ReadonlyArray<string>;
};

export type SettingsNavItem = {
    readonly id: string;
    readonly label: string;
    readonly to: string;
    readonly icon: ComponentType<{ readonly className?: string }>;
    readonly gate?: SettingsNavGate;
    /**
     * Survives the org-scope check, so every member of the organization sees
     * it rather than only those who can manage settings. For a page whose
     * whole API surface already admits any member.
     */
    readonly availableToAllMembers?: boolean;
    /**
     * Keeps a route discoverable in settings search without adding it to the
     * settings sidebar.
     */
    readonly searchOnly?: boolean;
    readonly keywords?: ReadonlyArray<string>;
    readonly keywordSettings?: ReadonlyArray<SettingsKeywordSetting>;
};

export type SettingsNavGroup = {
    readonly label: string; // '' for the unlabeled Personal group
    readonly scope: 'org' | 'personal';
    readonly items: ReadonlyArray<SettingsNavItem>;
};

export type SettingsNavGates = {
    readonly agentV3: boolean;
    readonly coordinatorAutomations: boolean;
    readonly canManageOrgSettings: boolean;
    // Phoebe staff, not an agency admin. Gates internal-only diagnostics that
    // sit inside the customer settings shell.
    readonly internalAdmin: boolean;
    readonly canAccessDataSync: boolean;
    readonly caregiverEngagement: boolean;
    readonly careCoordinator: boolean;
    readonly clockInOut: boolean;
    readonly dailyDigest: boolean;
    readonly phoneSetup: boolean;
    readonly slack: boolean;
    readonly multiOffice: boolean;
};

export type SettingsNavSearchResult = {
    readonly groupLabel: string;
    readonly item: SettingsNavItem;
    readonly section: string | null;
    readonly tabName: string;
    readonly label: string;
};

export const SETTINGS_NAV: ReadonlyArray<SettingsNavGroup> = [
    {
        label: 'Administration',
        scope: 'org',
        items: [
            {
                id: 'team-members',
                label: 'Team',
                to: '/settings/team-members',
                icon: Users,
                keywords: ['invite', 'members', 'people', 'team members'],
                keywordSettings: [
                    {
                        section: 'Team members',
                        keywords: ['members', 'people'],
                    },
                    {
                        section: 'Invite user',
                        keywords: ['invite'],
                    },
                ],
            },
            {
                id: 'skip-allow-lists',
                label: 'Skip allow list',
                to: '/settings/skip-allow-lists',
                icon: SkipForward,
                keywords: [
                    'allow list',
                    'callout clients',
                    'client filters',
                    'filters',
                    'open shift clients',
                    'skip list',
                ],
                keywordSettings: [
                    {
                        section: 'Caregiver filters',
                        keywords: ['allow list', 'skip list'],
                    },
                    {
                        section: 'Client filters',
                        keywords: [
                            'allow list',
                            'callout clients',
                            'clients to skip',
                            'open shift clients',
                            'skip list',
                        ],
                    },
                    {
                        section: 'Coordinator filters',
                        keywords: ['allow list', 'skip list'],
                    },
                ],
            },
            {
                id: 'care-coordinators',
                label: 'Care coordination',
                to: '/settings/care-coordinators',
                icon: Users,
                gate: 'careCoordinator',
                keywords: ['care coordinators', 'coordinators'],
                keywordSettings: [
                    {
                        section: 'Care coordinators',
                        keywords: ['coordinators'],
                    },
                ],
            },
            {
                id: 'change-history',
                label: 'Change history',
                to: '/settings/change-history',
                icon: History,
                gate: 'internalAdmin',
                keywords: [
                    'audit',
                    'change history',
                    'changes',
                    'history',
                    'who changed',
                ],
            },
        ],
    },
    {
        label: 'Personal',
        scope: 'personal',
        items: [
            {
                id: 'notifications',
                label: 'Notifications',
                to: '/settings/notifications',
                icon: Bell,
                keywords: [
                    'activity notifications',
                    'alerts',
                    'automatic cleanup',
                    'email',
                    'mark as read',
                    'office assignment',
                    'offices',
                    'personal notifications',
                    'sms',
                ],
                keywordSettings: [
                    {
                        section: 'Office assignment',
                        keywords: ['offices'],
                    },
                    {
                        section: 'Notification preferences',
                        keywords: ['activity notifications', 'alerts'],
                    },
                    {
                        section: 'Notification channels',
                        keywords: ['email', 'sms', 'browser', 'sound'],
                    },
                    {
                        section: 'Notification phone number',
                        keywords: ['phone number', 'text messages'],
                    },
                ],
            },
        ],
    },
    {
        label: 'Phone',
        scope: 'org',
        items: [
            {
                id: 'phone-instructions',
                label: 'Phone setup',
                to: '/settings/phone-instructions',
                icon: FileText,
                gate: 'phoneSetup',
                keywords: [
                    'connect phone',
                    'phone provider',
                    'phoebe number',
                    'phone setup',
                    'phone system',
                    'ringcentral',
                    'template email',
                    'vendor email',
                    'voip',
                ],
            },
            {
                id: 'phone-call-handling',
                label: 'Call handling',
                to: '/settings/phone',
                icon: PhoneCall,
                keywords: [
                    'after hours',
                    'business hours',
                    'call flow',
                    'call handling',
                    'daily digest email',
                    'recap of calls',
                    'routing tree',
                    'schedule',
                    'summary email',
                    'transfer options',
                ],
                keywordSettings: [
                    {
                        section: 'Callers',
                        keywords: ['caregivers only', 'unknown callers'],
                    },
                    {
                        section: 'Behavior',
                        keywords: [
                            'shift cancellations',
                            'transfer resistance',
                            'transfer behavior',
                        ],
                    },
                    {
                        section: 'Answer schedule',
                        keywords: ['business hours', 'schedule'],
                    },
                ],
            },
            {
                id: 'phone-pronunciations',
                label: 'Pronunciations',
                to: '/settings/pronunciations',
                icon: Languages,
                keywords: [
                    'city',
                    'dictionary',
                    'name',
                    'phonetic',
                    'pronunciation',
                    'town',
                ],
            },
        ],
    },
    {
        label: 'Features',
        scope: 'org',
        items: [
            {
                id: 'phoebe',
                label: 'Phoebe',
                to: '/settings/phoebe',
                icon: PhoebeFlowerIcon,
                // Every endpoint this page calls admits any member of the
                // organization, so the nav should not be stricter than the API.
                availableToAllMembers: true,
                keywords: [
                    'auto draft',
                    'automations',
                    'auto-fill shifts',
                    'last minute shifts',
                    'phoebe',
                    'unattended',
                ],
                keywordSettings: [
                    {
                        section: 'Fill open shifts',
                        keywords: [
                            'auto draft',
                            'check schedule',
                            'open shifts',
                        ],
                    },
                    {
                        section: 'Handle last minute shifts',
                        keywords: ['call-outs', 'last minute', 'triggers'],
                    },
                ],
            },
            {
                id: 'agency-context',
                label: 'Agency Guide',
                to: '/settings/agency-context',
                icon: FileText,
                availableToAllMembers: true,
                keywords: [
                    'about us',
                    'agency guide',
                    'agency context',
                    'agency details',
                    'agency facts',
                    'facts',
                    'offices',
                    'what phoebe knows',
                ],
                keywordSettings: [
                    {
                        section: 'Agency Guide',
                        keywords: ['facts', 'what phoebe knows'],
                    },
                ],
            },
            {
                id: 'outreach',
                label: 'Scheduling',
                to: '/settings/outreach-automation',
                icon: MessageSquare,
                keywords: [
                    'auto assign',
                    'auto reply',
                    'auto send',
                    'automations',
                    'call templates',
                    'callout templates',
                    'open shifts',
                    'outreach',
                    'outreach automation',
                    'outreach templates',
                    'recurring shift templates',
                    'voice outreach',
                    'voice templates',
                ],
                keywordSettings: [
                    {
                        section: 'Outreach automation',
                        keywords: ['auto assign', 'auto send'],
                    },
                    {
                        section: 'Open shift responses',
                        keywords: ['auto reply', 'declines'],
                    },
                    {
                        section: 'Messages',
                        keywords: ['call templates', 'voice templates'],
                    },
                    {
                        section: 'Client notifications',
                        keywords: ['client call', 'filled shifts'],
                    },
                    {
                        section: 'Care Plan Summary',
                        keywords: ['care plan', 'client instructions'],
                    },
                ],
            },
            {
                id: 'clock-in-out',
                label: 'Clock in / Clock out',
                to: '/settings/clock-in-out',
                icon: CalendarClock,
                keywords: [
                    'clock out',
                    'clock out templates',
                    'clock reminder messages',
                    'clock reminders',
                    'timekeeping',
                    'timekeeping templates',
                ],
                keywordSettings: [
                    {
                        section: 'Clock reminder messages',
                        keywords: ['clock reminders', 'timekeeping'],
                    },
                    {
                        section: 'Clock reminder templates',
                        keywords: ['clock out templates'],
                    },
                ],
            },
            {
                id: 'quiet-hours',
                label: 'Quiet hours',
                to: '/settings/quiet-hours',
                icon: Moon,
                keywords: [
                    'caregiver calls',
                    'do not call',
                    'outbound calls',
                    'quiet hours',
                    'calling hours',
                ],
            },
            {
                id: 'shift-confirmations',
                label: 'Shift confirmation',
                to: '/settings/shift-confirmations',
                icon: SquarePen,
                keywords: [
                    'confirmation automation',
                    'confirmation messages',
                    'confirmation schedule',
                    'confirmation templates',
                    'confirmations',
                    'no response',
                    'reply wait',
                    'shift confirmations',
                ],
                keywordSettings: [
                    {
                        section: 'Confirmation schedule',
                        keywords: ['schedule'],
                    },
                    {
                        section: 'No-response alerts',
                        keywords: ['no response', 'wait'],
                    },
                    {
                        section: 'Confirmation messages',
                        keywords: ['templates'],
                    },
                    {
                        section: 'Auto-reply settings',
                        keywords: ['automatic replies'],
                    },
                ],
            },
            {
                id: 'cancellations',
                label: 'Cancellations',
                to: '/settings/cancellations',
                icon: CalendarX,
                keywords: [
                    'callout',
                    'cancel shift',
                    'cancellations',
                    'review threshold',
                    'shift cancellations',
                    'sms cancellations',
                    'voice cancellations',
                ],
                keywordSettings: [
                    {
                        section: 'SMS cancellations',
                        keywords: ['cancel by text', 'sms cancellations'],
                    },
                    {
                        section: 'Voice cancellations',
                        keywords: ['cancel by phone', 'voice cancellations'],
                    },
                    {
                        section: 'Cancellation notice',
                        keywords: ['cancelled shifts message', 'templates'],
                    },
                ],
            },
            {
                id: 'caregiver-engagement',
                label: 'Caregiver engagement',
                to: '/settings/caregiver-engagement',
                icon: HeartHandshake,
                gate: 'caregiverEngagement',
                keywords: [
                    'anniversaries',
                    'birthdays',
                    'caregiver birthday',
                    'caregiver engagement',
                    'caregiver stats',
                    'check-ins',
                    'congratulations',
                    'engagement texts',
                    'milestones',
                    'monthly check-ins',
                    'monthly recap',
                    'monthly stats',
                    'recognition',
                    'retention',
                ],
                keywordSettings: [
                    {
                        section: 'Monthly caregiver stats',
                        keywords: ['monthly recap', 'monthly stats'],
                    },
                    {
                        section: 'Caregiver birthdays',
                        keywords: ['birthdays', 'birthday texts'],
                    },
                    {
                        section: 'Monthly caregiver check-ins',
                        keywords: ['check-ins', 'monthly check-ins'],
                    },
                    {
                        section: 'Caregiver/client milestones',
                        keywords: [
                            'anniversaries',
                            'congratulations',
                            'milestones',
                            'retention',
                        ],
                    },
                ],
            },
            {
                id: 'recommendations',
                label: 'Recommendations',
                to: '/settings/recommendations',
                icon: Sparkles,
                keywords: ['caregiver matching', 'matching defaults'],
                keywordSettings: [
                    {
                        section: 'Matching defaults',
                        keywords: ['caregiver matching'],
                    },
                    {
                        section: 'Caregiver tag thresholds',
                        keywords: ['engaged', 'unresponsive'],
                    },
                ],
            },
            {
                id: 'tags',
                label: 'Tags',
                to: '/settings/tags',
                icon: Tag,
                keywords: ['tag descriptions', 'attributes', 'match criteria'],
                keywordSettings: [
                    {
                        section: 'Tag meanings',
                        keywords: ['tag descriptions', 'attributes'],
                    },
                    {
                        section: 'Matters',
                        keywords: ['importance', 'required', 'match criteria'],
                    },
                ],
            },
            {
                id: 'activity-feed',
                label: 'Inbox activity settings',
                to: '/settings/activity-feed',
                icon: Activity,
                keywords: [
                    'activity',
                    'feed',
                    'instructions',
                    'notifications',
                    'urgent',
                ],
                keywordSettings: [
                    {
                        section: 'Instructions for Phoebe',
                        keywords: [
                            'include',
                            'instructions',
                            'notifications',
                            'urgent',
                        ],
                    },
                    {
                        section: 'Activity events',
                        keywords: ['feed', 'notifications'],
                    },
                    {
                        section: 'Voice call notes',
                        keywords: ['action required', 'priority'],
                    },
                    {
                        section: 'Auto cleanup',
                        keywords: ['auto address', 'stale items'],
                    },
                ],
            },
            {
                id: 'slack',
                label: 'Slack Integration',
                to: '/settings/slack',
                icon: Hash,
                gate: 'slack',
                keywords: ['integration', 'slack notifications'],
                keywordSettings: [
                    {
                        section: 'Public channels',
                        keywords: ['slack channels'],
                    },
                    {
                        section: 'Notifications',
                        keywords: ['slack notifications'],
                    },
                    {
                        section: 'Outreach cards',
                        keywords: ['slack outreach'],
                    },
                ],
            },
            {
                id: 'daily-digest',
                label: 'Daily digest',
                to: '/settings/daily-digest',
                icon: FileText,
                gate: 'dailyDigest',
                keywords: ['daily email', 'digest email', 'metrics report'],
                keywordSettings: [
                    {
                        section: 'Digest recipients',
                        keywords: ['daily email', 'digest email'],
                    },
                    {
                        section: 'Metrics report',
                        keywords: ['operations brief'],
                    },
                ],
            },
            {
                id: 'automations',
                label: 'Routines',
                to: '/routines',
                icon: Zap,
                gate: 'coordinatorAutomations',
                searchOnly: true,
                keywords: ['automations', 'configurations'],
            },
        ],
    },
    {
        label: 'Resources',
        scope: 'org',
        items: [
            {
                id: 'data-sync',
                label: 'Data sync',
                to: '/settings/data-sync',
                icon: Database,
                gate: 'canAccessDataSync',
                keywords: ['ehr', 'sync'],
                keywordSettings: [
                    {
                        section: 'EHR connection',
                        keywords: ['sync'],
                    },
                ],
            },
            {
                id: 'early-access',
                label: 'Early access',
                to: '/settings/early-access',
                icon: Cpu,
                keywords: ['beta', 'feature flags'],
                keywordSettings: [
                    {
                        section: 'Feature flags',
                        keywords: ['beta', 'early access'],
                    },
                    {
                        section: 'Phoebe chat approvals',
                        keywords: [
                            'approval',
                            'autopilot',
                            'permissions',
                            'review actions',
                        ],
                    },
                ],
            },
            {
                id: 'test-mode',
                label: 'Test Mode',
                to: '/settings/test-mode',
                icon: Zap,
                keywords: ['sandbox', 'testing'],
                keywordSettings: [
                    {
                        section: 'Sandbox testing',
                        keywords: ['test mode'],
                    },
                ],
            },
            {
                id: 'chrome-extension',
                label: 'Chrome Extension',
                to: '/settings/chrome-extension',
                icon: Puzzle,
                keywords: ['browser extension', 'extension install'],
                keywordSettings: [
                    {
                        section: 'Chrome extension',
                        keywords: ['browser extension', 'extension install'],
                    },
                ],
            },
            {
                id: 'help-center',
                label: 'Help Center',
                to: '/settings/help-center',
                icon: CircleHelp,
                keywords: [
                    'chat',
                    'docs',
                    'documentation',
                    'intercom',
                    'live chat',
                    'support',
                ],
                keywordSettings: [
                    {
                        section: 'Documentation',
                        keywords: ['docs', 'guides'],
                    },
                    {
                        section: 'Live chat',
                        keywords: ['chat', 'intercom', 'support'],
                    },
                ],
            },
        ],
    },
];

/**
 * Returns only the nav groups and items visible to the current user.
 *
 * Gating semantics:
 * 1. In an org-scoped group, a user who cannot manage settings keeps only the
 *    items marked `availableToAllMembers`.
 * 2. Within each remaining group, drop items whose gate is not enabled.
 * 3. Drop groups that become empty after item filtering.
 */
export function visibleSettingsNav(
    gates: SettingsNavGates,
): ReadonlyArray<SettingsNavGroup> {
    return filterSettingsNav(gates, false);
}

function filterSettingsNav(
    gates: SettingsNavGates,
    includeSearchOnly: boolean,
): ReadonlyArray<SettingsNavGroup> {
    return SETTINGS_NAV.flatMap((group) => {
        const scoped =
            group.scope === 'org' && !gates.canManageOrgSettings
                ? group.items.filter((item) => item.availableToAllMembers)
                : group.items;
        const items = scoped.filter(
            (item) =>
                (includeSearchOnly || !item.searchOnly) &&
                (!item.gate || gates[item.gate]),
        );
        if (items.length === 0) {
            return [];
        }
        return [{ ...group, items }];
    });
}

/**
 * Returns the scope that owns the given settings path, or null when no nav item
 * matches (e.g. the bare `/settings` index). Lets the sidebar reflect the active
 * route on cold load / deep link instead of a frozen initial value.
 */
export function settingsNavPathIsActive(
    itemTo: string,
    pathname: string,
): boolean {
    if (itemTo === pathname) {
        return true;
    }
    return (
        (itemTo === '/settings/notifications' ||
            itemTo === '/settings/team-members') &&
        pathname.startsWith(`${itemTo}/`)
    );
}

export function settingsScopeForPath(path: string): 'org' | 'personal' | null {
    for (const group of SETTINGS_NAV) {
        if (
            group.items.some(
                (item) =>
                    !item.searchOnly && settingsNavPathIsActive(item.to, path),
            )
        ) {
            return group.scope;
        }
    }
    return null;
}

export function findSettingsNavItemByPath(
    pathname: string,
): SettingsNavItem | null {
    for (const group of SETTINGS_NAV) {
        for (const item of group.items) {
            if (
                !item.searchOnly &&
                settingsNavPathIsActive(item.to, pathname)
            ) {
                return item;
            }
        }
    }
    return null;
}

/**
 * Returns the path of the first visible section in a scope, or null when the
 * scope has no visible sections. Used by the settings index route to pick a
 * role-aware landing section.
 */
export function firstVisibleSettingsPath(
    gates: SettingsNavGates,
    scope: 'org' | 'personal',
): string | null {
    const group = visibleSettingsNav(gates).find((g) => g.scope === scope);
    return group?.items[0]?.to ?? null;
}

/**
 * Flattens the visible settings nav into tab- and section-level search results.
 * Returns all keyword settings when the query is empty. Otherwise, setting
 * keywords win first; tab labels, group labels, and tab-level keywords expand
 * to that tab's settings when no individual setting matched.
 */
export function searchSettingsNav(
    query: string,
    gates: SettingsNavGates,
): ReadonlyArray<SettingsNavSearchResult> {
    const groups = filterSettingsNav(gates, true);
    const trimmedLower = query.trim().toLowerCase();
    const results: Array<SettingsNavSearchResult> = [];
    for (const group of groups) {
        for (const item of group.items) {
            const itemIdentitySearchableText = [
                group.label,
                item.label,
                item.id.replace(/-/g, ' '),
            ]
                .join(' ')
                .toLowerCase();
            const itemKeywordSearchableText = (item.keywords ?? [])
                .join(' ')
                .toLowerCase();
            const itemIdentityMatches =
                trimmedLower === '' ||
                itemIdentitySearchableText.includes(trimmedLower);
            const itemKeywordMatches =
                itemKeywordSearchableText.includes(trimmedLower);
            const itemMatches = itemIdentityMatches || itemKeywordMatches;
            const keywordSettings = item.keywordSettings ?? [];
            const matchingKeywordSettings = keywordSettings.filter(
                (setting) => {
                    const settingSearchableText = [
                        setting.section,
                        ...(setting.keywords ?? []),
                    ]
                        .join(' ')
                        .toLowerCase();
                    return settingSearchableText.includes(trimmedLower);
                },
            );
            const settingsToRender = itemIdentityMatches
                ? keywordSettings
                : matchingKeywordSettings.length > 0
                  ? matchingKeywordSettings
                  : itemMatches
                    ? keywordSettings
                    : [];
            if (settingsToRender.length > 0) {
                for (const setting of settingsToRender) {
                    results.push({
                        groupLabel: group.label,
                        item,
                        section: setting.section,
                        tabName: item.label,
                        label: `${item.label} > ${setting.section}`,
                    });
                }
                continue;
            }
            if (itemMatches) {
                results.push({
                    groupLabel: group.label,
                    item,
                    section: null,
                    tabName: item.label,
                    label: item.label,
                });
            }
        }
    }
    return results;
}

/**
 * Groups settings-nav search matches back under their nav groups. Returns the
 * full visible nav for an empty query; otherwise only groups that contain a
 * matching keyword setting, trimmed to their matching items.
 */
export function searchSettingsNavGroups(
    query: string,
    gates: SettingsNavGates,
): ReadonlyArray<SettingsNavGroup> {
    if (query.trim() === '') {
        return visibleSettingsNav(gates);
    }
    const matchedIds = new Set(
        searchSettingsNav(query, gates).map(({ item }) => item.id),
    );
    return filterSettingsNav(gates, true).flatMap((group) => {
        const items = group.items.filter((item) => matchedIds.has(item.id));
        return items.length === 0 ? [] : [{ ...group, items }];
    });
}
